import { useTranslation } from 'react-i18next'
import { Button, Press } from '@dorixona/ui'
import { Sheet } from '../../ui/Sheet'
import { Icon } from '../../ui/Icon'
import { ApiGapNote } from '../../ui/Gaps'
import type { Category } from './api'
import { type Filters, RX_FILTERS, SORT_KEYS, type SortKey, filterCount } from './model'

export interface FilterSheetProps {
  opened: boolean
  filters: Filters
  categories: Category[]
  category: number
  sort: SortKey
  /** Сколько товаров покажет кнопка: число или «10 000+», когда сервер упёрся в потолок. */
  found: string
  onChange: (filters: Filters) => void
  onCategory: (id: number) => void
  onSort: (sort: SortKey) => void
  onReset: () => void
  onClose: () => void
}

/** Фильтры каталога. На телефоне — шторка снизу, как в прототипе. */
export function FilterSheet({
  opened,
  filters,
  categories,
  category,
  sort,
  found,
  onChange,
  onCategory,
  onSort,
  onReset,
  onClose,
}: FilterSheetProps) {
  const { t } = useTranslation(['catalog', 'common'])
  const active = filterCount(filters)

  return (
    <Sheet opened={opened} label={t('catalog:filters')} onClose={onClose}>
      <div className="fsheet">
        <div className="fhead">
          <h2>{t('catalog:filters')}</h2>
          <Press
            squish="icon"
            className="fclose"
            aria-label={t('common:actions.close')}
            onPress={onClose}
          >
            <Icon name="i-x" size={18} />
          </Press>
        </div>

        <div className="fgroup" role="group" aria-label={t('catalog:categories')}>
          <div className="fl">{t('catalog:categories')}</div>
          <div className="fopts">
            <Press
              squish="chip"
              className={category === 0 ? 'fopt on' : 'fopt'}
              onPress={() => {
                onCategory(0)
              }}
            >
              {t('catalog:allProducts')}
            </Press>
            {categories.map((item) => (
              <Press
                key={item.id}
                squish="chip"
                className={category === item.id ? 'fopt on' : 'fopt'}
                onPress={() => {
                  onCategory(item.id)
                }}
              >
                {item.name}
              </Press>
            ))}
          </div>
        </div>

        <div className="fgroup" role="group" aria-label={t('catalog:release')}>
          <div className="fl">{t('catalog:release')}</div>
          <div className="fopts">
            {RX_FILTERS.map((value) => (
              <Press
                key={value}
                squish="chip"
                className={filters.rx === value ? 'fopt on' : 'fopt'}
                onPress={() => {
                  onChange({ ...filters, rx: value })
                }}
              >
                {t(`catalog:rxFilter.${value}`)}
              </Press>
            ))}
          </div>
        </div>

        <div className="fgroup" role="group" aria-label={t('catalog:availability')}>
          <div className="fl">{t('catalog:availability')}</div>
          <label className="fcheck2">
            <input
              type="checkbox"
              checked={filters.stock}
              onChange={(event) => {
                onChange({ ...filters, stock: event.target.checked })
              }}
            />
            {t('catalog:onlyInStock')}
          </label>
          <ApiGapNote what="Фильтр «со скидкой»" where="GET /catalog/products">
            Старой цены у товара в API нет, поэтому и отобрать товары со скидкой нечем.
          </ApiGapNote>
        </div>

        <div className="fgroup" role="group" aria-label={t('catalog:price')}>
          <div className="fl">{t('catalog:price')}</div>
          <div className="frow2">
            <input
              type="number"
              min="0"
              step="1000"
              inputMode="numeric"
              placeholder={t('catalog:priceFrom')}
              aria-label={t('catalog:priceFrom')}
              value={filters.pmin}
              onChange={(event) => {
                onChange({ ...filters, pmin: event.target.value })
              }}
            />
            <input
              type="number"
              min="0"
              step="1000"
              inputMode="numeric"
              placeholder={t('catalog:priceTo')}
              aria-label={t('catalog:priceTo')}
              value={filters.pmax}
              onChange={(event) => {
                onChange({ ...filters, pmax: event.target.value })
              }}
            />
          </div>
        </div>

        <div className="fgroup" role="group" aria-label={t('catalog:sort')}>
          <div className="fl">{t('catalog:sort')}</div>
          <select
            className="fsort"
            aria-label={t('catalog:sort')}
            value={sort}
            onChange={(event) => {
              onSort(event.target.value as SortKey)
            }}
          >
            {SORT_KEYS.map((value) => (
              <option key={value} value={value}>
                {t(`catalog:sorts.${value}`)}
              </option>
            ))}
          </select>
        </div>

        <div className="ffoot">
          {active > 0 ? (
            <Button variant="ghost" onPress={onReset}>
              {t('catalog:reset')}
            </Button>
          ) : null}
          <Button onPress={onClose}>{`${t('catalog:apply')} (${found})`}</Button>
        </div>
      </div>
    </Sheet>
  )
}
